import React, { useEffect, useState } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";

import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import { Table, TableBody, TableCell, TableHead, TableRow, TablePagination, Box, TextField, IconButton, Button } from '@material-ui/core';
import EnhancedTableCell, { getComparator } from "../../../components/EnhancedTableCell/EnhancedTableCell.comp";

import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

import { toastWarning, formatDate } from "../../../utils/utils";
import ProductDialog from './components/ProductDialog.comp';
import {
    listProductDefinitionsService,
    createProductDefinitionService,
    updateProductDefinitionService,
    deleteProductDefinitionService
} from "../../../services/pac.service";

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    headerInputGroup: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    tr_act: {
        background: '#edf4ff',
    },
    tr_d_act: {
        background: '#f8dede'
    },
    table_head_bg: {
        background: '#4285f4'
    },
    table_cell_white: {
        color: 'white',
    },
}));

const headers = [
    { id: "product_key", label: "Product Key", alignment: "left", sort: true },
    { id: "label", label: "Label", alignment: "left", sort: true },
    { id: "description", label: "Description", alignment: "left", sort: true },
    { id: "order_no", label: "Order", alignment: "left", sort: true },
    { id: "is_active", label: "Global Status", alignment: "left", sort: true },
    { id: "createdAt", label: "Created", alignment: "left", sort: true },
    { id: "updatedAt", label: "Updated", alignment: "left", sort: true },
];

function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const ProductsList = () => {
    const classes = useStyles();
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("order_no");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchText, setSearchText] = useState('');

    const [loading, setLoading] = useState(false);
    const [pageData, setPageData] = useState([]);

    const [objectData, setObjectData] = useState(undefined);
    const [openModal, setOpenModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const loadPageData = async () => {
        setLoading(true);
        try {
            const result = await listProductDefinitionsService();
            if (result.data && result.data.products) {
                setPageData(result.data.products);
            }
        } catch (e) {
            // error already toasted by interceptor
        }
        setLoading(false);
    };

    useEffect(() => {
        loadPageData();
    }, []);

    const searchRegex = new RegExp(escapeRegExp(searchText), 'i');
    const filteredRows = pageData.filter((row) => {
        return Object.keys(row).some((field) => {
            if (!row[field]) return false;
            return searchRegex.test(row[field].toString());
        });
    });

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const openCreateModal = () => {
        setObjectData(undefined);
        setModalTitle('Create Product Definition');
        setOpenModal(true);
    };

    const openEditModal = (record) => {
        setObjectData(record);
        setModalTitle('Update Product Definition');
        setOpenModal(true);
    };

    const invokeUpdateOrCreateService = async (data) => {
        setLoading(true);
        try {
            if (data._id) {
                await updateProductDefinitionService(data);
            } else {
                await createProductDefinitionService(data);
            }
            setOpenModal(false);
            setObjectData(undefined);
            loadPageData();
        } catch (e) {
            // error toasted
        }
        setLoading(false);
    };

    const invokeDeleteService = async (product_key) => {
        if (!window.confirm(`Are you sure you want to delete ${product_key}?`)) return;
        setLoading(true);
        try {
            await deleteProductDefinitionService(product_key);
            loadPageData();
        } catch (e) {
            // error toasted
        }
        setLoading(false);
    };

    const slicedRows = filteredRows
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Container component="main" maxWidth={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <div className={classes.headerInputGroup}>
                    <Button variant="contained" color="primary" onClick={openCreateModal}>
                        Create Product
                    </Button>
                </div>
                <div>
                    <Box display={'flex'} justifyContent={'flex-end'} mt={2}>
                        <TextField
                            variant="outlined"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search…"
                            InputProps={{
                                startAdornment: <SearchIcon fontSize="small" />,
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        style={{ visibility: searchText ? 'visible' : 'hidden' }}
                                        onClick={() => setSearchText('')}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                )
                            }}
                        />
                    </Box>
                </div>
            </div>
            
            <div className={classes.paper}>
                <h3>Product Definitions</h3>
            </div>

            <Table>
                <TableHead className={classes.table_head_bg}>
                    <TableRow>
                        {headers.map((item) =>
                            <EnhancedTableCell
                                key={item.id}
                                id={item.id}
                                className={classes.table_cell_white}
                                alignment={item.alignment}
                                sort={item.sort}
                                orderBy={orderBy}
                                order={order}
                                onRequestSort={handleRequestSort}
                            >
                                {item.label}
                            </EnhancedTableCell>
                        )}
                        <TableCell className={classes.table_cell_white}>Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {slicedRows.map((item) => (
                        <TableRow key={item._id} className={item.is_active ? classes.tr_act : classes.tr_d_act}>
                            <TableCell><strong>{item.product_key}</strong></TableCell>
                            <TableCell>{item.label}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{item.order_no}</TableCell>
                            <TableCell>{item.is_active ? 'Active' : 'Disabled'}</TableCell>
                            <TableCell>{formatDate(item.createdAt)}</TableCell>
                            <TableCell>{formatDate(item.updatedAt)}</TableCell>
                            <TableCell>
                                <div>
                                    <DeleteIcon onClick={() => invokeDeleteService(item.product_key)}
                                                className={'meta-data-table-action-btn'} fontSize={'large'} color={'error'} />
                                    <EditIcon onClick={() => openEditModal(item)}
                                              className={'meta-data-table-action-btn'} fontSize={'large'} color={'primary'} />
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <ProductDialog
                open={openModal}
                title={modalTitle}
                handleClose={() => setOpenModal(false)}
                submitCallback={invokeUpdateOrCreateService}
                dataObject={objectData}
            />

            <LoaderWithBackDrop loading={loading} />
        </Container>
    );
};

export default ProductsList;
